import { yupResolver } from '@hookform/resolvers/yup'
import { Controller, useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { NavLink, useNavigate, useSearchParams } from 'react-router-dom'

import { useEffect } from 'react'
import { toast } from 'react-toastify'
import { AuthLayout } from '../../components/AuthLayout'
import { Button } from '../../components/Button'
import { PasswordInput } from '../../components/FormElements/PasswordInput'
import { TOAST_ERROR_DEFAULT_OPTIONS } from '../../constants/toast'
import { useAuth } from '../../contexts/AuthContext'
import { api } from '../../services/api'
import { getErrorMessage } from '../../utils/getErrorMessage'
import { createResetPasswordSchema } from '../../validators/resetPasswordSchema'

type FormValues = {
  password: string
  passwordConfirmation: string
}

const schema = createResetPasswordSchema({
  password: {
    min: 'INVALID_PASSWORD',
    matches: 'INVALID_PASSWORD',
    identical: 'PASSWORDS_MUST_BE_IDENTICAL',
    required: 'REQUIRED_FIELD',
  },
  passwordConfirmation: {
    identical: 'PASSWORDS_MUST_BE_IDENTICAL',
    required: 'REQUIRED_FIELD',
  },
})

export const RegisterPassword = () => {
  const navigate = useNavigate()

  const [searchParams] = useSearchParams()

  const { signIn, signOut } = useAuth()
  const { formatMessage } = useIntl()
  const { control, formState, handleSubmit } = useForm<FormValues>({
    mode: 'onChange',
    resolver: yupResolver(schema),
  })

  const handleRegisterPassword = async (values: FormValues) => {
    try {
      const token = searchParams.get('token')
      const response = await api.post('/auth/reset-password', {
        ...values,
        token,
      })

      await signIn({
        email: response.data.email,
        password: values.password,
      })

      navigate('/expert/accept-terms')
    } catch (error) {
      toast.error(getErrorMessage(error), TOAST_ERROR_DEFAULT_OPTIONS)
    }
  }

  useEffect(() => {
    signOut()
  }, [])

  return (
    <AuthLayout
      title={formatMessage({ id: 'REGISTER_NEW_PASSWORD' })}
      withNavbar
    >
      <form
        onSubmit={handleSubmit(handleRegisterPassword)}
        className="flex flex-col justify-between h-full grow"
      >
        <section className="md:mb-14">
          <div className="mb-4">
            <Controller
              name="password"
              control={control}
              render={({
                field: { value = '', onChange },
                fieldState: { invalid, error },
              }) => (
                <PasswordInput
                  isValid={!invalid}
                  label={formatMessage({ id: 'ENTER_YOUR_NEW_PASSWORD' })}
                  autoComplete="new-password"
                  value={value}
                  onChange={onChange}
                  errorMessage={
                    error?.message && formatMessage({ id: error.message })
                  }
                />
              )}
            />
          </div>

          <div>
            <Controller
              name="passwordConfirmation"
              control={control}
              render={({
                field: { value = '', onChange },
                fieldState: { invalid, error },
              }) => (
                <PasswordInput
                  isValid={!invalid}
                  label={formatMessage({ id: 'TYPE_PASSWORD_AGAIN' })}
                  autoComplete="new-password"
                  value={value}
                  onChange={onChange}
                  errorMessage={
                    error?.message && formatMessage({ id: error.message })
                  }
                />
              )}
            />
          </div>
        </section>

        <footer className="flex flex-col gap-2 md:flex-row-reverse">
          <div className="flex-1">
            <Button
              disabled={!formState.isValid}
              isLoading={formState.isSubmitting}
              type="submit"
              fullWidth
            >
              {formatMessage({ id: 'REGISTER_PASSWORD' })}
            </Button>
          </div>

          <div className="flex-1">
            <NavLink to={'/expert'}>
              <Button
                disabled={formState.isSubmitting}
                type="button"
                variant="outline"
                fullWidth
              >
                {formatMessage({ id: 'CANCEL' })}
              </Button>
            </NavLink>
          </div>
        </footer>
      </form>
    </AuthLayout>
  )
}
